import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';

export interface FormSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Section heading. */
  title?: string;
  /** Optional description below the title. */
  subtitle?: string;
  /** Show a top border divider. Defaults to `true`. */
  divider?: boolean;
  /**
   * Title style variant:
   * - `'label'` (default): 12px uppercase muted label
   * - `'heading'`: 20px normal-weight heading (matches legacy Heading h5 with Gray color)
   */
  titleVariant?: 'label' | 'heading';
  semanticId?: string;
  children: React.ReactNode;
}

const TRANSIENT = ['$divider'] as const;
const shouldForward = (prop: string | number): boolean =>
  !TRANSIENT.includes(prop as (typeof TRANSIENT)[number]);

const Root = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $divider: boolean;
}>`
  width: 100%;

  ${({ $divider }) =>
    $divider &&
    css`
      margin-top: ${rem(24)};
      padding-top: ${rem(24)};
      border-top: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
    `}
`;

const SectionTitle = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(18)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: ${rem(16)};
`;

const SectionHeading = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(20)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: #595959;
  margin-bottom: ${rem(12)};
`;

const SectionSubtitle = styled.p`
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
  margin: ${rem(-8)} 0 ${rem(16)} 0;
`;

export const FormSection = React.forwardRef<HTMLDivElement, FormSectionProps>(
  function FormSection({ title, subtitle, divider = true, titleVariant = 'label', semanticId, children, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    const TitleComponent = titleVariant === 'heading' ? SectionHeading : SectionTitle;

    return (
      <Root ref={ref} $divider={divider} {...semanticProps} {...rest}>
        {title != null && title !== '' && <TitleComponent>{title}</TitleComponent>}
        {subtitle != null && subtitle !== '' && <SectionSubtitle>{subtitle}</SectionSubtitle>}
        {children}
      </Root>
    );
  },
);

FormSection.displayName = 'FormSection';
